import type { City, Place } from '../world/city';
import type { PhysicsCity } from '../physics/world';
import type { Person, Population } from './population';
interface Guard {
  person: Person;
  places: Place[];
  phase: number;
  sequence: number;
  task: string;
  retry: number;
  checks: number;
  observations: number;
  closed: boolean;
  reason: string;
}
/** Original finite lifeguards physically alternate a rescue-equipment check and
 * an assigned dry shore lookout. The common controller owns travel and damage. */
export class CoastStaff {
  units: Guard[] = [];
  private epoch = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.reset();
  }
  clear(): void {
    for (const u of this.units)
      this.population.civil?.cancelTask(u.person, 'Coastal shift lifecycle cleared');
    this.units = [];
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    const civil = this.population.civil;
    if (!civil) return;
    for (const post of this.city.coast?.guards ?? []) {
      if (!post.places.every((p) => civil.network.available(p))) continue;
      const person = this.population.createCrew(`crew:${post.id}`, post.spawn);
      civil.resume(person, 'street');
      person.controlled = true;
      person.state = 'wait';
      person.name = 'Linden lifeguard';
      person.color = 0xb86f58;
      this.units.push({
        person,
        places: post.places,
        phase: 0,
        sequence: 0,
        task: '',
        retry: 0,
        checks: 0,
        observations: 0,
        closed: false,
        reason: 'Starting the equipment check',
      });
    }
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch || !this.population.civil) return;
    const civil = this.population.civil;
    for (const u of this.units) {
      u.retry -= dt;
      if (u.closed || ['incapacitated', 'falling', 'flee'].includes(u.person.state)) continue;
      if (!u.places.every((p) => civil.network.available(p))) {
        civil.cancelTask(u.person, 'Coastal post or its equipment is damaged');
        u.closed = true;
        this.population.evacuate(u.person, u.places[0]);
        u.reason = 'Post closed; original guard seeking safety';
        continue;
      }
      const current = civil.task(u.person),
        task = current?.token === u.task ? current : undefined;
      if (u.task && task?.state === 'active') {
        u.reason = task.reason;
        continue;
      }
      if (u.task && task?.state === 'completed') {
        if (u.phase === 0) u.checks++;
        else u.observations++;
        u.phase = (u.phase + 1) % u.places.length;
        u.task = '';
        u.retry = 0.2;
      } else if (u.task) {
        u.task = '';
        u.retry = 3;
      }
      if (u.retry > 0) continue;
      const target = u.places[u.phase],
        token = `coast:${u.person.id}:${++u.sequence}`;
      if (
        civil.assignTask(
          u.person,
          target,
          token,
          u.phase === 0 ? 'inspecting rescue equipment' : 'watching water',
          target.duration,
        )
      ) {
        u.task = token;
        u.reason = 'Walking to the reserved coastal duty';
      } else {
        u.retry = 2;
        u.reason = 'Waiting for the coastal interaction to become available';
      }
    }
  }
  snapshot(): Record<string, number> {
    return {
      lifeguards: this.units.length,
      coastEquipmentChecks: this.units.reduce((n, u) => n + u.checks, 0),
      coastObservations: this.units.reduce((n, u) => n + u.observations, 0),
      coastPostsClosed: this.units.filter((u) => u.closed).length,
    };
  }
  inspect(): unknown[] {
    return this.units.map((u) => ({
      id: u.person.id,
      activity: u.person.state,
      position: u.person.p.toArray(),
      destination: u.places[u.phase].id,
      checks: u.checks,
      observations: u.observations,
      closed: u.closed,
      reason: u.reason,
    }));
  }
}
